/**
 * Configuration and resolution logic for the post-submission referral modal.
 *
 * Supports two audience variants:
 * 1. 'business-owner' (default)
 * 2. 'virtual-assistant'
 */

/**
 * List of ISO-2 country codes classified as Virtual Assistant locations.
 * The product team can populate known virtual-assistant country codes here (e.g. "PH").
 * If no countries are listed or the country cannot be resolved, audience safely defaults
 * to 'business-owner'.
 */
export const VA_COUNTRY_CODES = [
  "PH", // Philippines
];

/**
 * Resolves whether the current user is a "business-owner" or "virtual-assistant".
 *
 * Priority order:
 * 1. Explicit audience in session data / backend response (e.g. sessionData.audience)
 * 2. Explicit audience or role in user profile (e.g. user.audience, user.role, user.is_va)
 * 3. Country-based classification if user.country or sessionData.country matches VA_COUNTRY_CODES
 * 4. Safe default: "business-owner"
 *
 * @param {object} [user] - The authenticated user object from Context or API
 * @param {object} [sessionData] - The response data from submit-link or pod session
 * @returns {"business-owner" | "virtual-assistant"}
 */
export const resolveReferralAudience = (user, sessionData) => {
  // 1. Explicit audience returned by backend
  if (
    sessionData?.audience === "virtual-assistant" ||
    sessionData?.audience === "business-owner"
  ) {
    return sessionData.audience;
  }

  // 2. Explicit audience field on user
  if (
    user?.audience === "virtual-assistant" ||
    user?.audience === "business-owner"
  ) {
    return user.audience;
  }

  // Explicit role or flags on user
  if (typeof user?.role === "string") {
    const normalizedRole = user.role.toLowerCase().trim();
    if (
      normalizedRole === "virtual-assistant" ||
      normalizedRole === "virtual_assistant" ||
      normalizedRole === "va" ||
      normalizedRole === "assistant"
    ) {
      return "virtual-assistant";
    }
  }

  if (user?.is_va === true || user?.is_assistant === true) {
    return "virtual-assistant";
  }

  // 3. Country-derived classification (via user or session data)
  const country = (
    user?.country ||
    sessionData?.country ||
    ""
  )
    .toUpperCase()
    .trim();

  if (country && VA_COUNTRY_CODES.includes(country)) {
    return "virtual-assistant";
  }

  // 4. Safe default
  return "business-owner";
};

/**
 * Derives the account owner's first name for a virtual assistant.
 * Looks for workspace owner / account owner relationships on the user model.
 *
 * @param {object} [user] - The authenticated user object
 * @returns {string | undefined}
 */
export const getAccountOwnerFirstName = (user) => {
  if (!user) return undefined;

  const rawName =
    user.accountOwnerFirstName ||
    user.account_owner_first_name ||
    user.owner_first_name ||
    user.account_owner?.first_name ||
    user.workspace_owner_first_name;

  if (rawName && typeof rawName === "string" && rawName.trim()) {
    return rawName.trim();
  }

  return undefined;
};

/**
 * Derives the signed-in user's first name for a business owner.
 *
 * @param {object} [user] - The authenticated user object
 * @returns {string | undefined}
 */
export const getUserFirstName = (user) => {
  if (!user) return undefined;

  const rawName =
    user.firstName ||
    user.first_name ||
    user.linkedin_first_name;

  if (rawName && typeof rawName === "string" && rawName.trim()) {
    return rawName.trim();
  }

  return undefined;
};

/**
 * Audience-specific content variants.
 */
export const referralVariants = {
  "business-owner": {
    getHeading: (firstName) =>
      firstName
        ? `${firstName}, grow better together`
        : "Grow better together",
    description:
      "Bring three friends to Engage AI—and let your referrals pay for your subscription. More friends in the community to support your posts and grow your reach. You use Engage AI for free.",
    cta: "Invite 3 friends",
    url: "https://apps.engage-ai.co/ReferAndEarn",
  },
  "virtual-assistant": {
    getHeading: (accountOwnerFirstName) =>
      accountOwnerFirstName
        ? `${accountOwnerFirstName}’s VA, turn your network into cash`
        : "Turn your network into cash",
    description:
      "Invite people to Engage AI and earn cash for every successful referral. The more people you bring into the community, the more money you can earn.",
    cta: "Start earning cash",
    url: "https://engage-ai.co/affiliate/",
  },
};
